/**
 * useBridge Hook
 *
 * React hook for components to interact with the UE plugin bridge.
 * Provides typed command methods and connection status.
 *
 * Usage:
 *   const { isConnected, getProperty, setProperty, callFunction, subscribe } = useBridge()
 *
 *   // Get a property
 *   const version = await getProperty('system.version')
 *
 *   // Subscribe to events (auto-cleanup in useEffect)
 *   useEffect(() => {
 *     const unsub = subscribe('module.installed', (data) => { ... })
 *     return unsub
 *   }, [subscribe])
 *
 *   // Phase 6: Reflection commands
 *   const manifest = await getManifest('ACharacter')
 *   const index = await getManifestIndex()
 *   const { assets } = await assetList('StaticMeshActor')
 */

import { useContext, useCallback } from 'react'
import { BridgeContext } from './bridgeContext'
import type {
  AssetEntry,
  ManifestIndexEntry,
  ClassManifest,
  BatchResult,
  WorldContext,
} from './reflectionTypes'

export function useBridge() {
  const { client, isConnected } = useContext(BridgeContext)

  const getProperty = useCallback(
    async (target: string, worldContext?: WorldContext): Promise<unknown> => {
      if (!client) throw new Error('Bridge not connected')
      return client.getProperty(target, worldContext)
    },
    [client],
  )

  const setProperty = useCallback(
    async (target: string, value: unknown, worldContext?: WorldContext): Promise<void> => {
      if (!client) throw new Error('Bridge not connected')
      return client.setProperty(target, value, worldContext)
    },
    [client],
  )

  const callFunction = useCallback(
    async (
      target: string,
      params?: unknown,
      worldContext?: WorldContext,
      timeoutMs?: number,
    ): Promise<unknown> => {
      if (!client) throw new Error('Bridge not connected')
      return client.callFunction(target, params, worldContext, timeoutMs)
    },
    [client],
  )

  const sendStateMessage = useCallback(
    async (stateType: string, params: Record<string, unknown>): Promise<unknown> => {
      if (!client) throw new Error('Bridge not connected')
      return client.sendStateMessage(stateType, params)
    },
    [client],
  )

  const subscribe = useCallback(
    (topic: string, callback: (data: unknown) => void): (() => void) => {
      if (!client) return () => {}
      return client.subscribe(topic, callback)
    },
    [client],
  )

  // --- Phase 6: Reflection commands ---

  const assetList = useCallback(
    async (className: string): Promise<{ assets: AssetEntry[]; total: number }> => {
      if (!client) throw new Error('Bridge not connected')
      return client.assetList(className)
    },
    [client],
  )

  const getManifestIndex = useCallback(
    async (): Promise<ManifestIndexEntry[]> => {
      if (!client) throw new Error('Bridge not connected')
      return client.getManifestIndex()
    },
    [client],
  )

  const getManifest = useCallback(
    async (className: string): Promise<ClassManifest> => {
      if (!client) throw new Error('Bridge not connected')
      return client.getManifest(className)
    },
    [client],
  )

  const batch = useCallback(
    async (commands: unknown[], abortOnFail?: boolean): Promise<BatchResult> => {
      if (!client) throw new Error('Bridge not connected')
      return client.batch(commands, abortOnFail)
    },
    [client],
  )

  const lockObject = useCallback(
    async (objectPath: string): Promise<void> => {
      if (!client) throw new Error('Bridge not connected')
      return client.lockObject(objectPath)
    },
    [client],
  )

  const unlockObject = useCallback(
    async (objectPath: string): Promise<void> => {
      if (!client) throw new Error('Bridge not connected')
      return client.unlockObject(objectPath)
    },
    [client],
  )

  // --- Asset CRUD commands ---

  const createAsset = useCallback(
    async (assetPath: string, className: string, properties?: Record<string, unknown>): Promise<unknown> => {
      if (!client) throw new Error('Bridge not connected')
      return client.createAsset(assetPath, className, properties)
    },
    [client],
  )

  const deleteAsset = useCallback(
    async (assetPath: string): Promise<void> => {
      if (!client) throw new Error('Bridge not connected')
      return client.deleteAsset(assetPath)
    },
    [client],
  )

  const renameAsset = useCallback(
    async (sourcePath: string, destPath: string): Promise<unknown> => {
      if (!client) throw new Error('Bridge not connected')
      return client.renameAsset(sourcePath, destPath)
    },
    [client],
  )

  const duplicateAsset = useCallback(
    async (sourcePath: string, destPath: string): Promise<unknown> => {
      if (!client) throw new Error('Bridge not connected')
      return client.duplicateAsset(sourcePath, destPath)
    },
    [client],
  )

  const importAsset = useCallback(
    async (filePath: string, destPath: string): Promise<unknown> => {
      if (!client) throw new Error('Bridge not connected')
      return client.importAsset(filePath, destPath)
    },
    [client],
  )

  const saveAsset = useCallback(
    async (assetPath: string): Promise<void> => {
      if (!client) throw new Error('Bridge not connected')
      return client.saveAsset(assetPath)
    },
    [client],
  )

  const getInstalledModules = useCallback(
    async (): Promise<unknown> => {
      if (!client) throw new Error('Bridge not connected')
      return client.getInstalledModules()
    },
    [client],
  )

  return {
    isConnected,
    // Phase 2 commands
    getProperty,
    setProperty,
    callFunction,
    sendStateMessage,
    subscribe,
    // Phase 6 commands
    assetList,
    getManifestIndex,
    getManifest,
    batch,
    lockObject,
    unlockObject,
    // Asset CRUD commands
    createAsset,
    deleteAsset,
    renameAsset,
    duplicateAsset,
    importAsset,
    saveAsset,
    getInstalledModules,
  }
}
